import { Paid, PriceCheck } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlPlannerShiftCompletionStatus } from "~/__generated__";

import { ShiftItemPaymentsTableFragment } from "./fragments.generated";

type Props = {
    shift: ShiftItemPaymentsTableFragment;
};

export default function ShiftItemPaymentBadge({ shift }: Props) {
    const { t } = useTranslation("fieldworker");
    const rows = shift.payments ?? [];
    const status = shift.summary?.completionStatus;

    const hiddenStatuses = [GqlPlannerShiftCompletionStatus.AwaitingReport, GqlPlannerShiftCompletionStatus.Planned];

    if (!status || hiddenStatuses.includes(status)) {
        return null;
    }

    if (rows.length === 0) {
        if (shift.readyForPayment) {
            return (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <PriceCheck color="primary" />
                    <Typography variant="caption" fontWeight="bold" color="primary">
                        {t("shiftItem.labelShiftReadyForPayment")}
                    </Typography>
                </Stack>
            );
        }

        const statusMessages: Partial<Record<GqlPlannerShiftCompletionStatus, string>> = {
            [GqlPlannerShiftCompletionStatus.Approved]: t("shiftItem.labelShiftToBePaid"),
            [GqlPlannerShiftCompletionStatus.AwaitingApproval]: t("shiftItem.labelShiftToBeApproved"),
        };

        const message = statusMessages[status] || t("shiftItem.labelNoPayment");

        return (
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Paid />
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    {message}
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack direction="row" alignItems="center" gap={0.5}>
            <Paid color="primary" />
            <Typography variant="caption" fontWeight="bold" color="primary">
                {t("shiftItem.labelShiftPaid")}
            </Typography>
        </Stack>
    );
}
